import React, { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { ErrorBoundary } from '@sentry/react';
import { UseMutationResult } from '@tanstack/react-query';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

//helpers
import {
    generateBreadcrumbs,
    getInvalidSegmentRedirect,
} from '../../nav/helpers/breadcrumbHelpers';

import { useAuthSession } from '../../../hooks/useAuthSession';
import useRoleFlags from 'hooks/auth/useRoleFlags';

import GlobalError from '../../errors/GlobalErrorBoundary';
import Error from '../../errors/LoadingError';
import Breadcrumb, { BreadcrumbItem } from '../../nav/Breadcrumb';
import LoadingSpinner from '../../nav/LoadingSpinner';
// components
import TitleBar from './titleBar';

interface PageBasicProps {
    title?: React.ReactNode;
    subTitle?: string;
    description?: React.ReactNode;
    style?: React.CSSProperties;
    isLoading?: boolean;
    isUpdating?: boolean;
    backText?: string;
    backLink?: string;
    isError?: boolean;
    error?: string;
    betaMode?: boolean;
    actionBar?: React.ReactNode;
    headerStyle?: React.CSSProperties;
    onTitleChange?: (value: string) => void;
    allowEmptyTitle?: boolean;
    onTitleChangeMutation?: UseMutationResult<any, any, any, any>;
    titleLabel?: React.ReactNode;
    titleToolTip?: string;
    isSecondary?: boolean;
    childStyle?: React.CSSProperties;
    childClass?: string;
    isSubPage?: boolean;
    customBreadcrumbs?: BreadcrumbItem[];
    disableBreadcrumbs?: boolean;
    currentBreadcrumb?: BreadcrumbItem;
    showInfoIcon?: boolean;
    onInfoIconClick?: () => void;
    showBetaModeBanner?: boolean;
    headerMeta?: React.ReactNode;
}

const PageBasic: FC<PropsWithChildren<PageBasicProps>> = ({
    title = '',
    subTitle,
    description,
    style = {},
    children,
    isLoading = false,
    isUpdating = false,
    backText,
    backLink,
    isError = false,
    error = 'Something went wrong.',
    betaMode = false,
    actionBar,
    headerStyle,
    onTitleChange,
    allowEmptyTitle,
    onTitleChangeMutation,
    titleLabel,
    titleToolTip,
    isSecondary,
    childStyle,
    isSubPage = false,
    customBreadcrumbs,
    disableBreadcrumbs,
    currentBreadcrumb,
    showInfoIcon,
    onInfoIconClick,
    showBetaModeBanner,
    headerMeta,
    childClass,
}) => {
    const { hasWriteAccess } = useRoleFlags();
    const { user: currentUser } = useAuthSession();
    const history = useHistory();

    const isDisabled = useMemo<boolean>(() => {
        return !hasWriteAccess;
    }, [hasWriteAccess]);

    useEffect(() => {
        const redirect = getInvalidSegmentRedirect(location.pathname);
        if (redirect) {
            history.replace(redirect);
        }
    }, [history]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const breadcrumbs = useMemo(() => {
        if (!disableBreadcrumbs) {
            if (customBreadcrumbs) return customBreadcrumbs;
            const data = generateBreadcrumbs(location.pathname);
            if (currentBreadcrumb) {
                data.push(currentBreadcrumb);
            }
            return data;
        }
        return undefined;
    }, [currentBreadcrumb, disableBreadcrumbs, customBreadcrumbs]);

    if (betaMode && (!currentUser || !currentUser.aam))
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '50vh',
                }}
            >
                <h4>We're building things here, come back later.</h4>
            </div>
        );

    return (
        <ErrorBoundary fallback={GlobalError}>
            <ContainerDiv style={style} isSubPage={isSubPage}>
                {breadcrumbs && <Breadcrumb breadcrumbs={breadcrumbs} />}
                {titleLabel}
                <TitleContainer headerStyle={headerStyle}>
                    <TitleBar
                        title={title}
                        subTitle={subTitle}
                        description={description}
                        isUpdating={isUpdating && !isLoading}
                        backText={backText}
                        backLink={backLink}
                        onTitleChange={onTitleChange}
                        allowEmptyTitle={allowEmptyTitle}
                        onTitleChangeMutation={onTitleChangeMutation}
                        titleToolTip={titleToolTip}
                        isSecondary={isSecondary}
                        isDisabled={isDisabled}
                        showInfoIcon={showInfoIcon}
                        onInfoIconClick={onInfoIconClick}
                        showBetaModeBanner={showBetaModeBanner}
                    />
                    {actionBar}
                </TitleContainer>
                {headerMeta}
                <div style={childStyle} className={childClass}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : isError ? (
                        <Error errorMsg={error} />
                    ) : (
                        children
                    )}
                </div>
            </ContainerDiv>
        </ErrorBoundary>
    );
};

const ContainerDiv = styled.div.withConfig({
    shouldForwardProp: (prop) => !['isSubPage', 'style'].includes(prop),
})<{ isSubPage?: boolean; style?: React.CSSProperties }>(
    ({ isSubPage, style }) => ({
        margin: isSubPage ? 0 : aampeTheme.spacing4(5, 5),
        padding: isSubPage ? aampeTheme.spacing4(4, 4) : 0,
        backgroundColor: isSubPage
            ? aampeTheme.colors.white
            : aampeTheme.colors.backgroundWG02,
        ...style,
    }),
);

const TitleContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'headerStyle',
})<{ headerStyle?: React.CSSProperties }>(({ headerStyle }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    justifyContent: 'space-between',
    ...headerStyle,
}));

export default PageBasic;
